
import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  MenuItem,
  TableContainer,
} from "@mui/material";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

/* ===================== CONSTANT DATA ===================== */

const SESSIONS = ["2025-2026", "2024-25", "2023-24"];
const CLASSES = ["5th", "6th", "7th", "8th", "9th", "10th"];

const DASHBOARD_STATS = [
  {
    title: "Login",
    sub: "Total",
    today: 1,
    weekly: 9,
    monthly: 18,
    bg: "linear-gradient(135deg,#ffecd2,#fcb69f)",
  },
  {
    title: "Admissions",
    sub: "Total 0",
    today: 0,
    weekly: 0,
    monthly: 0,
    bg: "linear-gradient(135deg,#d4fc79,#96e6a1)",
  },
  {
    title: "Follow Ups",
    sub: "Total",
    today: 0,
    missed: 0,
    monthly: 0,
    bg: "linear-gradient(135deg,#84fab0,#8fd3f4)",
  },
  {
    title: "Due",
    sub: "Total",
    today: 0,
    weekly: 0,
    monthly: 0,
    bg: "linear-gradient(135deg,#a6c0fe,#f68084)",
  },
];

const ATTENDANCE_LINE_DATA = [
  { day: "1 Jan", present: 28, absent: 2 },
  { day: "2 Jan", present: 26, absent: 4 },
  { day: "3 Jan", present: 27, absent: 3 },
  { day: "4 Jan", present: 29, absent: 1 },
  { day: "5 Jan", present: 30, absent: 0 },
];

const ATTENDANCE_PIE_DATA = [
  { name: "Total Present", value: 140 },
  { name: "Total Absent", value: 10 },
];

const PIE_COLORS = ["#4f9cff", "#ff8f8f"];

const UNPAID_RECORDS = [
  {
    id: 1,
    receipt: "R001",
    admission: "A101",
    student: "Rahul Patil",
    class: "10th",
    roll: "12",
    mobile: "9999999999",
    email: "rahul@gmail.com",
    month: "Jan",
    session: "2025-2026",
    total: 5000,
    paid: 0,
  },
  {
    id: 2,
    receipt: "R002",
    admission: "A102",
    student: "Sneha Kulkarni",
    class: "9th",
    roll: "8",
    mobile: "8888888888",
    email: "sneha@gmail.com",
    month: "Jan",
    session: "2025-2026",
    total: 4500,
    paid: 2000,
  },
  {
    id: 3,
    receipt: "R003",
    admission: "A103",
    student: "Om Deshmukh",
    class: "8th",
    roll: "5",
    mobile: "7777777777",
    email: "om@gmail.com",
    month: "Jan",
    session: "2025-2026",
    total: 4000,
    paid: 1000,
  },
];

const AMOUNT_COLLECTION_DATA = [
  { date: "1 Jan", amount: 10 },
  { date: "2 Jan", amount: 25 },
  { date: "3 Jan", amount: 18 },
  { date: "4 Jan", amount: 30 },
  { date: "5 Jan", amount: 51 },
];

/* ===================== DASHBOARD ===================== */

function Dashboard() {
  const [session, setSession] = useState("2025-2026");
  const [selectedClass, setSelectedClass] = useState("");

  useEffect(() => {
    // 🔜 Future API calls:
    // fetchDashboardData(session, selectedClass)
  }, [session, selectedClass]);

  return (
    <Box>
      {/* ================= HEADER ================= */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4" fontWeight={700}>
          Dashboard
        </Typography>

        <TextField
          select
          size="small"
          label="Session"
          value={session}
          onChange={(e) => setSession(e.target.value)}
          sx={{ width: 160 }}
        >
          {SESSIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* ================= TOP CARDS ================= */}
     {/* ================= TOP CARDS ================= */}
<Grid container spacing={3}>
  {DASHBOARD_STATS.map((c, i) => (
    <Grid item xs={12} md={3} key={i}>
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          color: "#fff",
          background: c.bg,

          // Stronger card appearance
          boxShadow: "0 8px 20px rgba(0,0,0,0.28)",
          border: "1px solid rgba(255,255,255,0.18)",

          // Smooth hover effect
          transition: "all 0.2s ease",

          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 12px 25px rgba(0,0,0,0.35)",
          },
        }}
      >
        <Typography
          fontSize={20}
          fontWeight={700}
        >
          {c.title}
        </Typography>

        <Typography
          fontSize={34}
          fontWeight={800}
          sx={{ mt: 0.5, mb: 1 }}
        >
          {c.sub}
        </Typography>

        <Typography>
          Today {c.today}
        </Typography>

        <Typography>
          Weekly {c.weekly}
        </Typography>

        {"missed" in c && (
          <Typography>
            Missed {c.missed}
          </Typography>
        )}

        <Typography>
          Monthly {c.monthly}
        </Typography>
      </Paper>
    </Grid>
  ))}
</Grid>

      {/* ================= ATTENDANCE ================= */}
      <Paper sx={{ p: 3, mt: 5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          <Typography fontWeight={700}>Attendance</Typography>

          <Box display="flex" gap={2} alignItems="center">
            <TextField
              select
              size="small"
              label="Select Class"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              sx={{ width: 140 }}
            >
              {CLASSES.map((cls) => (
                <MenuItem key={cls} value={cls}>
                  {cls}
                </MenuItem>
              ))}
            </TextField>

            <Typography fontSize={14}>
              View Frequency :
              <Chip label="Monthly" size="small" sx={{ mx: 0.5 }} />
              <Chip label="Weekly" size="small" sx={{ mx: 0.5 }} />
              <Chip label="Daily" size="small" />
            </Typography>
          </Box>
        </Box>

        <Box height={280}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ATTENDANCE_LINE_DATA}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Line dataKey="present" stroke="#4f9cff" strokeWidth={3} />
              <Line dataKey="absent" stroke="#ff8f8f" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* ================= UNPAID + PIE ================= */}
      <Paper sx={{ p: 3, mt: 5 }}>
        <Typography fontWeight={700} mb={2}>
          Current Session Unpaid Record
        </Typography>

        <TableContainer sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Receipt</TableCell>
                <TableCell>Admission</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Roll</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Month</TableCell>
                <TableCell>Session</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Paid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {UNPAID_RECORDS.map((r, index) => (
                <TableRow key={r.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{r.receipt}</TableCell>
                  <TableCell>{r.admission}</TableCell>
                  <TableCell>{r.student}</TableCell>
                  <TableCell>{r.class}</TableCell>
                  <TableCell>{r.roll}</TableCell>
                  <TableCell>{r.mobile}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.month}</TableCell>
                  <TableCell>{r.session}</TableCell>
                  <TableCell>₹ {r.total}</TableCell>
                  <TableCell>
                    <Chip
                      label={`₹ ${r.paid}`}
                      color={r.paid === 0 ? "error" : "warning"}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* PIE */}
        <Box mt={4} display="flex" flexDirection="column" alignItems="center">
          <Typography fontWeight={700}>Total Attendance</Typography>
          <Box height={220} width={260}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={ATTENDANCE_PIE_DATA}
                  dataKey="value"
                  innerRadius={60}
                  outerRadius={90}
                >
                  {ATTENDANCE_PIE_DATA.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Paper>

      {/* ================= AMOUNT COLLECTION ================= */}
      <Paper sx={{ p: 3, mt: 5 }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontWeight={700}>Amount Collection</Typography>

          <Typography fontSize={14}>
            View Frequency :
            <Chip label="Monthly" size="small" sx={{ mx: 0.5 }} />
            <Chip label="Weekly" size="small" sx={{ mx: 0.5 }} />
            <Chip label="Yearly" size="small" />
          </Typography>
        </Box>

        <Box height={280}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={AMOUNT_COLLECTION_DATA}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line dataKey="amount" stroke="#7f53ac" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Box>
  );
}

export default Dashboard;
